// ---------------------------------------------------------------------------
// Tenant Admin module — Webhooks management page
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectContext } from '../lib/project-context';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { WebhookDeliveryLog } from '../components/WebhookDeliveryLog';
import { getDebugHeaders } from '../lib/api';
import { useFormatters } from '../hooks/useFormatters';
const apiBase = import.meta.env.VITE_API_BASE_URL || '';

// ---------------------------------------------------------------------------
// Types (API response shapes until api-client adds these methods)
// ---------------------------------------------------------------------------

interface WebhookEndpoint {
  id: string;
  tenantId: string;
  projectId: string | null;
  url: string;
  events: string[];
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface CreateWebhookResponse {
  data: WebhookEndpoint & { secret?: string };
}

interface OneTimeWebhookSecret {
  endpointUrl: string;
  secret: string | null;
  source: 'create' | 'rotate';
}

// ---------------------------------------------------------------------------
// One-time signing secret notice
// ---------------------------------------------------------------------------

function OneTimeSecretNotice({
  secret,
  onDismiss,
}: {
  secret: OneTimeWebhookSecret;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('admin');
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');

  const handleCopy = async () => {
    if (!secret.secret) return;

    try {
      await navigator.clipboard.writeText(secret.secret);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('error');
    }
  };

  if (!secret.secret) {
    return (
      <div
        className="mb-6 rounded-sm border border-warning/30 bg-warning-tint p-4 text-sm text-warning"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-medium">{t('webhooks.secret.savedNoSecretTitle')}</p>
            <p className="mt-1">
              {t('webhooks.secret.savedNoSecretMessage')}
            </p>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded px-2 py-1 text-ink hover:bg-warning-tint focus:outline-none focus:ring-2 focus:ring-warning"
          >
            {t('webhooks.secret.dismiss')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mb-6 rounded-sm border border-primary/30 bg-primary-tint p-4 text-sm text-primary"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-medium">
            {secret.source === 'rotate'
              ? t('webhooks.secret.rotatedTitle')
              : t('webhooks.secret.createdTitle')}
          </p>
          <p className="mt-1">
            {t('webhooks.secret.copyHint')}
          </p>
          <label htmlFor="webhook-one-time-secret" className="mt-3 block font-medium">
            {t('webhooks.secret.label', { url: secret.endpointUrl })}
          </label>
          <div className="mt-1 flex flex-col gap-2 sm:flex-row">
            <input
              id="webhook-one-time-secret"
              type="text"
              readOnly
              value={secret.secret}
              className="w-full rounded-sm border border-primary/40 bg-surface-raised px-3 py-2 font-mono text-sm text-ink focus:ring-2 focus:ring-primary focus:border-transparent"
              onFocus={(event) => event.currentTarget.select()}
            />
            <button
              type="button"
              onClick={handleCopy}
              className="rounded-sm bg-primary-hover px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              {t('webhooks.secret.copyButton')}
            </button>
          </div>
          {copyStatus === 'copied' && <p className="mt-2 text-primary">{t('webhooks.secret.copied')}</p>}
          {copyStatus === 'error' && (
            <p className="mt-2 text-danger">
              {t('webhooks.secret.copyError')}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded px-2 py-1 text-primary hover:bg-primary-tint focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {t('webhooks.secret.dismiss')}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Endpoint list columns
// ---------------------------------------------------------------------------

function useEndpointColumns(): Column<WebhookEndpoint>[] {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  return useMemo<Column<WebhookEndpoint>[]>(
    () => [
      {
        key: 'url',
        header: t('webhooks.columns.url'),
        render: (e) => (
          <span className="text-sm font-mono text-primary truncate block max-w-xs" title={e.url}>
            {e.url}
          </span>
        ),
      },
      {
        key: 'events',
        header: t('webhooks.columns.events'),
        render: (e) => (
          <div className="flex flex-wrap gap-1">
            {e.events.slice(0, 3).map((ev) => (
              <span
                key={ev}
                className="inline-block px-2 py-0.5 text-xs bg-primary-tint text-primary rounded"
              >
                {ev}
              </span>
            ))}
            {e.events.length > 3 && (
              <span className="text-xs text-ink-muted">
                {t('webhooks.messages.moreEvents', { count: e.events.length - 3 })}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'status',
        header: t('webhooks.columns.status'),
        render: (e) => <StatusBadge value={e.status} />,
      },
      {
        key: 'createdAt',
        header: t('webhooks.columns.created'),
        render: (e) => (
          <span className="text-sm text-ink-muted tabular-nums font-mono">
            {f.date(e.createdAt, { dateStyle: 'medium' })}
          </span>
        ),
      },
    ],
    [f, t],
  );
}

// ---------------------------------------------------------------------------
// Create endpoint form
// ---------------------------------------------------------------------------

function CreateEndpointForm({
  tenantId,
  onCreated,
}: {
  tenantId: string;
  onCreated: (secret: OneTimeWebhookSecret) => void;
}) {
  const { t } = useTranslation('admin');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const eventList = events
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      if (eventList.length === 0) {
        setError(t('webhooks.messages.eventsRequired'));
        setSubmitting(false);
        return;
      }

      const idempotencyKey = `wh_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

      const response = await fetch(`${apiBase}/v1/admin/tenants/${tenantId}/webhooks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          ...getDebugHeaders(),
        },
        body: JSON.stringify({ url, events: eventList }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          (body as { error?: { message?: string } })?.error?.message ?? `HTTP ${response.status}`,
        );
      }

      const body = (await response.json()) as CreateWebhookResponse;
      const createdUrl = body.data.url;

      setUrl('');
      setEvents('');
      onCreated({
        endpointUrl: createdUrl,
        secret: body.data.secret ?? null,
        source: 'create',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('webhooks.errors.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-surface-raised rounded-sm shadow p-6 mb-6">
      <h3 className="text-lg font-semibold text-ink mb-4">{t('webhooks.headings.form')}</h3>

      {error && (
        <div className="mb-4 p-3 bg-danger-tint border border-danger/30 rounded text-danger text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="webhook-url" className="block text-sm font-medium text-ink mb-1">
            {t('webhooks.form.url')}
          </label>
          <input
            id="webhook-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t('webhooks.form.urlPlaceholder')}
            required
            className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="webhook-events" className="block text-sm font-medium text-ink mb-1">
            {t('webhooks.form.events')}
          </label>
          <input
            id="webhook-events"
            type="text"
            value={events}
            onChange={(e) => setEvents(e.target.value)}
            placeholder={t('webhooks.form.eventsPlaceholder')}
            required
            className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? t('webhooks.actions.creating') : t('webhooks.actions.create')}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function WebhooksPage() {
  const { t } = useTranslation('admin');
  const endpointColumns = useEndpointColumns();
  const { tenantId } = useProjectContext();
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedEndpointId, setSelectedEndpointId] = useState<string | null>(null);
  const [oneTimeSecret, setOneTimeSecret] = useState<OneTimeWebhookSecret | null>(null);

  const loadEndpoints = useCallback((options?: { showPageLoading?: boolean }) => {
    const showPageLoading = options?.showPageLoading ?? true;

    if (!tenantId) {
      setEndpoints([]);
      setLoading(false);
      return;
    }

    if (showPageLoading) {
      setLoading(true);
    }
    setError(null);

    fetch(`${apiBase}/v1/admin/tenants/${tenantId}/webhooks`, {
      headers: { ...getDebugHeaders() },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { data: WebhookEndpoint[] };
        setEndpoints(body.data);
      })
      .catch((err: unknown) => {
        if (showPageLoading) {
          setError(err instanceof Error ? err.message : t('webhooks.errors.loadFailed'));
        }
      })
      .finally(() => {
        if (showPageLoading) {
          setLoading(false);
        }
      });
  }, [tenantId, t]);

  useEffect(() => {
    loadEndpoints();
  }, [loadEndpoints]);

  if (!tenantId) {
    return (
      <div className="p-6">
        <h1 className="font-display text-3xl text-ink mb-4">{t('webhooks.headings.list')}</h1>
        <div className="bg-primary-tint border border-primary/30 rounded-sm p-4 text-primary">
          <p className="text-sm">{t('webhooks.messages.selectProject')}</p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingState rows={3} />;
  if (error) return <ErrorState message={error} onRetry={loadEndpoints} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('webhooks.headings.list')}</h1>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover transition-colors"
        >
          {showCreate ? t('webhooks.actions.cancel') : t('webhooks.actions.newEndpoint')}
        </button>
      </div>

      {showCreate && (
        <CreateEndpointForm
          tenantId={tenantId}
          onCreated={(secret) => {
            setOneTimeSecret(secret);
            setShowCreate(false);
            loadEndpoints({ showPageLoading: false });
          }}
        />
      )}

      {oneTimeSecret && (
        <OneTimeSecretNotice
          secret={oneTimeSecret}
          onDismiss={() => setOneTimeSecret(null)}
        />
      )}

      <DataTable
        columns={endpointColumns}
        data={endpoints}
        rowKey={(e) => e.id}
        onRowClick={(e) =>
          setSelectedEndpointId(selectedEndpointId === e.id ? null : e.id)
        }
        emptyMessage={t('webhooks.empty')}
        ariaLabel={t('webhooks.ariaLabel')}
      />

      {selectedEndpointId && (
        <div className="mt-6">
          <WebhookDeliveryLog
            tenantId={tenantId}
            endpointId={selectedEndpointId}
          />
        </div>
      )}
    </div>
  );
}
